import type { WidgetApi } from './api';
import { WidgetTransport } from './transport';
import { isHumanChatSendRetry, type HumanChatSendRetry } from './humanChatSendRetry';
import { isHumanChatCommandRetries, type HumanChatCommandRetry } from './humanChatCommandRetry';

export interface HumanChatState {
  status: 'iva_active' | 'handoff_pending' | 'queued' | 'offered' | 'human_active' | 'wrap_up' | 'returning_to_iva' | 'closed';
  conversationId?: string;
  epoch?: number;
  initialSequence?: number;
}
export interface HumanChatMessage {
  id: string;
  sequence: number;
  epoch: number;
  sender: 'customer' | 'agent' | 'iva';
  visibility: 'public';
  text: string;
  createdAt: number;
}
export type HumanChatRequest =
  | { action: 'state' | 'renew' }
  | { action: 'negotiate' | 'resume'; conversationId: string }
  | { action: 'history'; conversationId: string; after: number }
  | { action: 'send'; conversationId: string; commandId: string; epoch: number; text: string }
  | { action: 'customer-cancel' | 'customer-end'; conversationId: string; commandId: string; epoch: number };

interface ChatCallbacks {
  state: (state: HumanChatState) => void;
  messages: (messages: HumanChatMessage[]) => void;
  error: (error: unknown) => void;
  token: (token: string) => void;
  returnReady?: (state: HumanChatState) => void;
  retry?: (retry: HumanChatSendRetry | undefined, accepted?: boolean) => void;
  commands?: (commands: HumanChatCommandRetry[]) => void;
}

export class HumanChatChannel {
  private stopped = false;
  private state: HumanChatState = { status: 'iva_active' };
  private cursor = 0;
  private pending: Promise<void> | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private transport: WidgetTransport | undefined;
  private transportEpoch: number | undefined;
  private transportExpires = 0;
  private nextRenewal = 0;
  private renewal: Promise<void> | undefined;
  private sending = false;
  private retry: HumanChatSendRetry | undefined;
  private commandRetries = new Map<string, HumanChatCommandRetry>();
  private commandsInFlight = new Set<string>();

  constructor(private readonly api: Pick<WidgetApi, 'chatCustomer'>, private token: string, private readonly callbacks: ChatCallbacks,
    private readonly transportFactory: () => WidgetTransport = () => new WidgetTransport(), private readonly clock: () => number = Date.now,
    initialSequence = 0, initialRetry?: HumanChatSendRetry, initialCommands: HumanChatCommandRetry[] = []) {
    if (!Number.isSafeInteger(initialSequence) || initialSequence < 0) throw new Error('CHAT_INVALID_CURSOR');
    if (initialRetry !== undefined && !isHumanChatSendRetry(initialRetry)) throw new Error('CHAT_INVALID_RETRY');
    this.retry = initialRetry ? { ...initialRetry } : undefined;
    if (!isHumanChatCommandRetries(initialCommands)) throw new Error('CHAT_INVALID_RETRY');
    for (const { action, commandId, conversationId, epoch } of initialCommands) this.commandRetries.set(commandId, { action, commandId, conversationId, epoch });
    this.cursor = initialSequence;
  }

  async start(): Promise<void> {
    await this.refresh();
    if (!this.stopped) this.schedule();
  }

  private schedule() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(async () => { await this.refresh(); if (!this.stopped) this.schedule(); }, 5000);
  }

  refresh(): Promise<void> {
    if (this.stopped) return Promise.resolve();
    if (this.pending) return this.pending;
    this.pending = this.load().catch(error => { if (!this.stopped) this.callbacks.error(error); }).finally(() => { this.pending = undefined; });
    return this.pending;
  }

  private async load() {
    const next = await this.request<HumanChatState>({ action: 'state' });
    if (this.stopped) return;
    const returned = next.status === 'iva_active' && !!next.conversationId;
    if (next.status === 'iva_active' && !returned) { this.state = next; this.callbacks.state(next); return; }
    if (!next.conversationId || !Number.isSafeInteger(next.epoch) || next.epoch! < 0
      || !['iva_active', 'handoff_pending', 'queued', 'offered', 'human_active', 'wrap_up', 'returning_to_iva', 'closed'].includes(next.status)) throw new Error('CHAT_INVALID_STATE');
    if (this.state.conversationId && this.state.conversationId !== next.conversationId) throw new Error('CHAT_CONVERSATION_CHANGED');
    if (this.retry && this.retry.conversationId !== next.conversationId) throw new Error('CHAT_CONVERSATION_CHANGED');
    if ([...this.commandRetries.values()].some(command => command.conversationId !== next.conversationId)) throw new Error('CHAT_CONVERSATION_CHANGED');
    this.state = next;
    this.callbacks.state(next);
    if (this.stopped) return;
    if (!returned && !['closed', 'wrap_up'].includes(next.status)) await this.renewToken();
    if (this.stopped) return;
    if (next.status === 'handoff_pending') return;
    let hasMore = false;
    for (let page = 0; page < 20; page++) {
      const history = await this.request<{ messages: HumanChatMessage[]; cursor: number; hasMore: boolean; state: HumanChatState }>(
        { action: 'history', conversationId: next.conversationId, after: this.cursor });
      if (this.stopped) return;
      if (!Array.isArray(history.messages) || !Number.isSafeInteger(history.cursor) || history.cursor < this.cursor
        || typeof history.hasMore !== 'boolean' || (history.hasMore && history.cursor === this.cursor)
        || history.messages.some((message, index) => message.visibility !== 'public'
          || !Number.isSafeInteger(message.sequence) || message.sequence <= (index ? history.messages[index - 1].sequence : this.cursor)
          || message.sequence > history.cursor || typeof message.text !== 'string' || !['iva', 'agent', 'customer'].includes(message.sender))) throw new Error('CHAT_INVALID_REPLAY');
      this.callbacks.messages(history.messages);
      this.cursor = history.cursor;
      this.state = { ...history.state, conversationId: next.conversationId };
      this.callbacks.state(this.state);
      if (this.stopped) return;
      hasMore = history.hasMore;
      if (!history.hasMore) break;
    }
    if (returned) {
      this.state = next;
      if (!hasMore) this.callbacks.returnReady?.(next);
      return;
    }
    if (['closed', 'wrap_up'].includes(this.state.status)) {
      if (!hasMore) await this.stop();
      return;
    }
    if (this.state.status === 'returning_to_iva') {
      if (!hasMore) this.callbacks.returnReady?.(this.state);
      return;
    }
    if (this.transportEpoch !== this.state.epoch || this.clock() >= this.transportExpires - 60_000) {
      const access = await this.request<{ url: string; epoch: number; expiresAt: string }>({ action: 'negotiate', conversationId: next.conversationId });
      if (this.stopped) return;
      await this.transport?.stop();
      if (this.stopped) return;
      const transport = this.transportFactory();
      this.transport = transport;
      transport.onMessage(data => { if (data?.type === 'chat.changed' && data.conversationId === this.state.conversationId) void this.refresh(); });
      await transport.connect(access.url);
      if (this.stopped) { await transport.stop(); return; }
      this.transportEpoch = access.epoch;
      this.transportExpires = Date.parse(access.expiresAt);
    }
  }

  private async request<T = unknown>(body: HumanChatRequest): Promise<T> {
    if (this.stopped) throw new Error('CHAT_NOT_ACTIVE');
    const token = this.token;
    try {
      return await this.api.chatCustomer<T>(token, body);
    } catch (error) {
      const failure = error as { status?: number; code?: string };
      if (this.stopped || failure.status !== 401 || failure.code !== 'CHAT_AUTH_REQUIRED') throw error;
      if (this.token === token) await this.renewToken(true);
      if (this.stopped) throw new Error('CHAT_NOT_ACTIVE');
      return this.api.chatCustomer<T>(this.token, body);
    }
  }

  private renewToken(force = false): Promise<void> {
    if (this.renewal) return this.renewal;
    if (this.stopped || (!force && this.clock() < this.nextRenewal)) return Promise.resolve();
    this.renewal = this.performRenewal().finally(() => { this.renewal = undefined; });
    return this.renewal;
  }

  private async performRenewal(): Promise<void> {
    const renewed = await this.api.chatCustomer<{ token: string; exp: number }>(this.token, { action: 'renew' });
    if (this.stopped) return;
    if (typeof renewed.token !== 'string' || !renewed.token || !Number.isSafeInteger(renewed.exp)
      || renewed.exp * 1000 <= this.clock()) throw new Error('CHAT_INVALID_TOKEN');
    this.token = renewed.token;
    this.callbacks.token(renewed.token);
    this.nextRenewal = Math.min(this.clock() + 10 * 60_000, renewed.exp * 1000 - 60_000);
  }

  async send(text: string): Promise<void> {
    if (this.stopped || this.sending || !this.state.conversationId || !['queued', 'offered', 'human_active'].includes(this.state.status)) throw new Error('CHAT_NOT_ACTIVE');
    if (this.retry && this.retry.text !== text) throw new Error('CHAT_SEND_UNCONFIRMED');
    this.sending = true;
    const request = this.retry?.text === text && this.retry.conversationId === this.state.conversationId ? this.retry
      : { text, commandId: crypto.randomUUID(), epoch: this.state.epoch!, conversationId: this.state.conversationId };
    this.retry = request;
    try {
      this.callbacks.retry?.({ ...request });
      await this.request({ action: 'send', ...request });
      if (this.stopped) return;
      this.retry = undefined;
      this.callbacks.retry?.(undefined, true);
      await this.refresh();
    } catch (error) {
      if (!this.stopped && (error as { code?: string })?.code === 'CHAT_STALE_ASSIGNMENT') {
        this.retry = undefined;
        this.callbacks.retry?.(undefined);
        await this.refresh();
      }
      throw error;
    } finally { this.sending = false; }
  }

  async command(action: 'customer-cancel' | 'customer-end', commandId: string): Promise<void> {
    if (this.stopped || !this.state.conversationId || this.state.epoch === undefined) throw new Error('CHAT_NOT_ACTIVE');
    if (this.commandsInFlight.has(commandId)) throw new Error('CHAT_COMMAND_BUSY');
    const prior = this.commandRetries.get(commandId);
    if (prior && (prior.action !== action || prior.conversationId !== this.state.conversationId)) throw new Error('CHAT_COMMAND_ID_REUSED');
    if ([...this.commandRetries.values()].some(command => command.action === action && command.commandId !== commandId)) throw new Error('CHAT_COMMAND_UNCONFIRMED');
    const request = prior || { action, commandId, conversationId: this.state.conversationId, epoch: this.state.epoch };
    if (!isHumanChatCommandRetries([request])) throw new Error('CHAT_INVALID_RETRY');
    this.commandRetries.set(commandId, request);
    this.commandsInFlight.add(commandId);
    try {
      this.publishCommands();
      await this.request({ ...request });
      if (this.stopped) throw new Error('CHAT_NOT_ACTIVE');
      this.commandRetries.delete(commandId);
      this.publishCommands();
    } catch (error) {
      if (!this.stopped && (error as { code?: string })?.code === 'CHAT_STALE_ASSIGNMENT') {
        this.commandRetries.delete(commandId);
        this.publishCommands();
        await this.refresh();
      }
      throw error;
    } finally { this.commandsInFlight.delete(commandId); }
    await this.refresh();
  }

  private publishCommands(): void {
    this.callbacks.commands?.([...this.commandRetries.values()].map(command => ({ ...command })));
  }

  async stop(): Promise<void> {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    const transport = this.transport;
    this.transport = undefined;
    try { await transport?.stop(); } catch {}
  }
}